import React, { useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { BuyModal } from "@/components/site/BuyModal";
import { IMG } from "@/data/content";
import { useContent } from "@/context/ContentContext";
import { slugify } from "@/lib/slug";
import { ArrowRight, ShoppingBag } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const resolveImg = (v) =>
  v && typeof v === "string" && v.startsWith("http") ? v : IMG[v] || v;

// Take the first paragraph or first ~180 chars for a preview
const previewText = (md) => {
  if (!md) return "";
  const firstParagraph = md.split(/\n{2,}/)[0] || "";
  // strip markdown symbols for a clean preview
  const clean = firstParagraph
    .replace(/^[-*]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/[*_`>#]/g, "")
    .trim();
  return clean.length > 180 ? clean.slice(0, 180).trimEnd() + "…" : clean;
};

const Products = () => {
  const { content } = useContent();
  const list = content.products || [];
  const brandName = content.brand?.name || "Geomantic Kalpataru";
  const [active, setActive] = useState(null);

  return (
    <div data-testid="products-page">
      <Toaster theme="dark" position="top-right" />
      <PageHero
        eyebrow="Sacred Offerings"
        title={<>Bespoke instruments of <em>transformation</em>.</>}
        subtitle="Every offering is hand-crafted, consecrated and tuned to the individual. Nothing is mass-produced."
        image={IMG.crystal}
      />

      <section className="section">
        <div className="wrap grid gap-8 md:grid-cols-2">
          {list.map((p, i) => {
            const slug = slugify(p.slug || p.name);
            return (
              <Reveal key={p.name + i} delay={i * 80}>
                <article className="glass lift overflow-hidden group flex flex-col h-full">
                  <Link to={`/products/${slug}`} className="block overflow-hidden" data-testid={`product-image-${i}`}>
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={resolveImg(p.image) || IMG.crystal}
                        alt={p.name}
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                      />
                    </div>
                  </Link>
                  <div className="p-8 flex flex-col flex-1">
                    <Link to={`/products/${slug}`} className="hover:text-gold transition">
                      <h3 className="font-display text-3xl text-ivory">{p.name}</h3>
                    </Link>
                    <p className="mt-3 text-ivory/65 leading-relaxed">{previewText(p.body)}</p>
                    <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-display text-3xl text-gold-grad">
                          ₹{p.priceInr || 100} <span className="text-lg text-ivory/50">· ${p.priceUsd || 2}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ivory/45 mt-1">
                          {p.price || "One-time · INR / USD"}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Link
                          to={`/products/${slug}`}
                          className="btn-ghost text-[10px]"
                          data-testid={`product-view-${i}`}
                        >
                          View Details <ArrowRight className="h-4 w-4" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => setActive(p)}
                          className="btn-gold text-[10px]"
                          data-testid={`product-buy-${i}`}
                        >
                          <ShoppingBag className="h-4 w-4" /> Buy Now
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {active && <BuyModal product={active} brandName={brandName} onClose={() => setActive(null)} />}
    </div>
  );
};

export default Products;
